import { t } from '@ctcells/i18n'
import { useEffect, useState } from 'react'

import { clockText } from './batchStripModel.ts'

/**
 * 2행 오른끝의 **걸린 시간** 조각 — eric 2026-09-28 「그거 초도 넣어주고」.
 *
 * 🔴 **초를 세는 자리는 여기 하나다.** 1초 틱이 이 조각 안에서만 돌아서 작업 줄 전체(버튼 · 진행바 ·
 *    E-STOP)는 매초 다시 그려지지 않는다. 컨테이너가 이 틱을 쥐면 줄 전체가 1초마다 돈다.
 * 🔴 **폴링이 아니다**(DP-01). 서버에 아무것도 묻지 않는다 — 화면 시계만 다시 읽는다.
 * 🔴 **폭이 흔들리지 않는다.** `num`(tabular-nums)이라 초가 바뀌어도 글자 폭이 같다 — 옆의 E-STOP 자리가
 *    흔들리면 안전 문제다.
 * ⚠ `sinceMs` 가 없으면(종착 = 소요 · 시험의 정적 값) 틱을 돌리지 않고 `text` 를 그대로 적는다.
 */
export function StripClock({
  text,
  kind,
  sinceMs,
  totalText,
}: {
  text: string
  kind: 'elapsed' | 'duration' | undefined
  sinceMs: number | undefined
  totalText: string | undefined
}) {
  const now = useSecondTick(sinceMs)
  const shown = sinceMs === undefined || now === undefined ? text : clockText(now - sinceMs)
  return (
    <span
      className="strip__elapsed num"
      title={t(kind === 'duration' ? 'strip.duration' : 'strip.elapsed', { time: shown })}
    >
      {shown}
      {totalText === undefined ? '' : ` / ${totalText}`}
    </span>
  )
}

const SECOND_MS = 1_000

/**
 * 기준 시각이 있을 때만 1초마다 「지금」을 다시 읽는다. 없으면 타이머가 없다.
 * 첫 틱 전에는 `undefined` 라 받은 글자(`text` — 컨테이너가 그린 순간의 값)가 선다.
 * ⚠ 읽은 값은 **그 기준 시각에 묶는다** — 기준이 바뀌면 옛 틱 값을 버린다(다른 배치의 시계를 잇지 않는다).
 */
function useSecondTick(sinceMs: number | undefined): number | undefined {
  const [tick, setTick] = useState<{ since: number; now: number } | undefined>(undefined)

  useEffect(() => {
    if (sinceMs === undefined) return undefined
    const id = setInterval(() => {
      setTick({ since: sinceMs, now: Date.now() })
    }, SECOND_MS)
    return () => {
      clearInterval(id)
    }
  }, [sinceMs])

  return tick !== undefined && tick.since === sinceMs ? tick.now : undefined
}
